import { DriveItem, ManufacturerItem } from '@/drive/interface'
import { getDrives, getManufacturers } from '@/drive/service'
import {
  deviceModeItems,
  getOptions,
  plcTypeItems,
  portNoItems
} from '@/shared/SelectData'
import { PlcItem, TemplatePlcEditProps } from '@/template/interface'
import { TemplateReference } from '@/template/reference'
import { getRefTypeDesc } from '@/template/reference/util'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import { Checkbox, Col, Collapse, Form, Input, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import { TemplatePlcAdvanced } from './advanced'
import { TemplatePlcEthernet } from './ethernet'
import { TemplatePlcSerial } from './serial'

const { Panel } = Collapse

export const TemplatePlcEdit: React.FC<TemplatePlcEditProps> = (
  props: TemplatePlcEditProps
) => {
  const [deviceMode, setDeviceMode] = useState<number>(0)
  const [enableBroadcast, setEnableBroadcast] = useState<boolean>(false)
  const [manufacturers, setManufacturers] = useState<ManufacturerItem[]>([])
  const [drives, setDrives] = useState<DriveItem[]>([])
  const [form] = Form.useForm()
  const [type, setType] = useState<string>(
    (props.formData && props.formData.item.type) || 'serial'
  )
  const [referenceVisible, setReferenceVisible] = useState<boolean>(false)
  const disabled = props.references && props.references.length > 0
  const typeMap = {
    serial: 0,
    ethernet: 1,
    local: 2
  }
  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 10 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 }
    }
  }

  const formatMessage = getFormatMessage()

  // 加载品牌
  const loadManufacturers = async (
    plcType: string,
    deviceMode: number
  ): Promise<ManufacturerItem[]> => {
    const items = await getManufacturers(typeMap[plcType], deviceMode)
    setManufacturers(items)
    return items
  }

  // 加载驱动
  const loadDrivers = async (
    plcType: string,
    deviceMode: number,
    manufacturer: string
  ): Promise<DriveItem[]> => {
    const items = await getDrives(typeMap[plcType], deviceMode, manufacturer)
    setDrives(items)
    return items
  }

  const changeType = (plcType: string) => {
    setType(plcType)
  }

  const changeManufacture = async (
    plcType: string,
    deviceMode: number,
    manufacturer: string
  ): Promise<void> => {
    const items = await loadDrivers(plcType, deviceMode, manufacturer)
    const plc: PlcItem = form.getFieldsValue()
    plc.manufacturer = manufacturer
    form.setFieldsValue(plc)
    changeDrive(items, items[0].plcName || '', deviceMode)
  }

  const changeDrive = (
    items: DriveItem[],
    plcName: string,
    deviceMode: number
  ) => {
    const drive = items?.find((x) => x.plcName == plcName)
    const plc: PlcItem = form.getFieldsValue()
    plc.plcName = plcName
    plc.alias = plcName
    if (drive) {
      plc.serial = drive.serial
      plc.ethernet = drive.ethernet || {}
      if (deviceMode == 1) {
        plc.ethernet.ip = '127.0.0.1'
      }
      plc.advance = getDriveAdvance(drive)
      plc['compatibleModels'] = drive.supportPlcs?.join(',')
    }
    if (plc.type == 'serial' && plc.serial) {
      plc.serial.portNo = props.formData?.portNoItems[0].value
    }
    form?.setFieldsValue(plc)
  }

  // 设备类型变更时触发
  const handleTypeChange = async (value: any) => {
    changeType(value)
    const items = await loadManufacturers(value, deviceMode)
    await changeManufacture(value, deviceMode, items[0].name)
  }

  // 品牌变更时触发
  const handleManufactureChange = async (value: string) => {
    await changeManufacture(type, deviceMode, value)
  }

  // 驱动变更时触发
  const handleDriveChange = (value: any) => {
    changeDrive(drives, value, deviceMode)
  }

  // 主从模式变化
  const handleDeviceModeChange = async (value: number) => {
    setDeviceMode(value)
    const items = await loadManufacturers(type, value)
    await changeManufacture(type, value, items[0].name)
  }

  const initEdit = async () => {
    const plcType = props.formData?.item.type || ''
    setType(plcType)
    setDeviceMode(props.formData?.item?.deviceMode || 0)
    await loadManufacturers(
      props.formData?.item.type || '',
      props.formData?.item?.deviceMode || 0
    )
    await loadDrivers(
      plcType,
      props.formData?.item.deviceMode || 0,
      props.formData?.item.manufacturer || ''
    )
    form.setFieldsValue(props?.formData?.item)
  }

  const initCreate = async () => {
    const item = props?.formData?.item || {}
    const plcType = item.type || 'serial'
    setType(plcType)
    const mode = props?.formData?.item.deviceMode || 0
    const mItems = await loadManufacturers(plcType, mode)
    if (mItems && mItems.length > 0) {
      const dItems = await loadDrivers(plcType, mode, mItems[0].name)
      item.manufacturer = mItems[0].name
      form.setFieldsValue(item)
      changeDrive(dItems, dItems[0].plcName || '', mode)
    }
  }

  useEffect(() => {
    // 如果是编辑
    if (props.formData && props.formData.item.id) {
      initEdit()
    } else {
      initCreate()
    }
  }, [props.formData])

  useEffect(() => {
    if (props.formResult) {
      form
        .validateFields()
        .then((values: PlcItem) => {
          const merged = Object.assign(props.formData?.item, values)
          if (props.onFormValidate) {
            if (!props.onFormValidate(true, merged)) return
          }
          if (props.onFormSubmit) {
            if (!merged.id) {
              merged.id = Guid.raw()
            }
            const drive = drives?.find((x) => x.plcName == merged.plcName)
            merged.plcId = drive?.plcId
            merged.enableBroadcast = enableBroadcast
            merged.registers = drive?.registers
            merged.drive = {
              soFileMd5: drive?.soFileMd5,
              soFileUrl: drive?.soFileUrl
            }
            props.onFormSubmit(merged)
            form.resetFields()
          }
        })
        .catch((info) => {
          console.log('Validate Failed:', info)
          if (props.onFormValidate) {
            props.onFormValidate(false)
          }
        })
    }
  }, [props.formResult])

  const getDriveAdvance = (drive: DriveItem) => {
    return {
      plcResponceTimeout: drive.plcResponceTimeout,
      protocolTimeout1: drive.protocolTimeout1,
      protocolTimeout2: drive.protocolTimeout2,
      maxPacketsWordReg: drive.maxPacketsWordReg,
      maxPacketsBitReg: drive.maxPacketsBitReg,
      assembleIntervalWordReg: drive.assembleIntervalWordReg,
      assembleIntervalBitReg: drive.assembleIntervalBitReg,
      protocolInterval: drive.protocolInterval,
      byteOrder16: drive.byteOrder?.byteOrder16,
      byteOrder32: drive.byteOrder?.byteOrder32,
      byteOrderFloat: drive.byteOrder?.byteOrderFloat,
      autoPackage: 0,
      errMsgDispTime: 0
    }
  }

  return (
    <div style={{ minHeight: 300, paddingTop: 10 }}>
      <Form form={form} {...formItemLayout}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({ id: 'app.template.plc.edit.type' })}
              name='type'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.plc.edit.type.message'
                  })
                }
              ]}
            >
              <Select
                disabled={disabled}
                onChange={handleTypeChange}
                value='serial'
              >
                {getOptions(props.formData?.plcTypeItems || plcTypeItems)}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.plc.edit.deviceMode'
              })}
              name='deviceMode'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.plc.edit.deviceMode.message'
                  })
                }
              ]}
            >
              <Select
                onChange={handleDeviceModeChange}
                value={deviceMode}
                disabled={disabled || type == 'local'}
              >
                {getOptions(deviceModeItems)}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.plc.edit.manufacturer'
              })}
              name='manufacturer'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.plc.edit.manufacturer.message'
                  })
                }
              ]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  option!.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                onChange={handleManufactureChange}
                disabled={disabled}
              >
                {manufacturers &&
                  manufacturers.map((x) => (
                    <Select.Option key={Guid.raw()} value={x.name}>
                      {x.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.plc.edit.plcName'
              })}
              name='plcName'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.plc.edit.plcName.message'
                  })
                }
              ]}
            >
              <Select
                onChange={(value) => handleDriveChange(value)}
                disabled={disabled}
              >
                {drives &&
                  drives.map((x) => (
                    <Select.Option key={Guid.raw()} value={x.plcName || ''}>
                      {x.plcName}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({ id: 'app.template.plc.edit.alias' })}
              name='alias'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.plc.edit.alias.message'
                  })
                }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            {deviceMode == 0 && type != 'local' && (
              <Form.Item
                label={
                  <span>
                    <Checkbox
                      checked={enableBroadcast}
                      onChange={(e) => setEnableBroadcast(e.target.checked)}
                    />
                    {formatMessage({
                      id: 'app.template.plc.edit.broadcastStationNo'
                    })}
                  </span>
                }
                name='broadcastStationNo'
              >
                {enableBroadcast && <Input />}
              </Form.Item>
            )}

            {deviceMode == 1 && type != 'local' && (
              <Form.Item
                label={formatMessage({
                  id: 'app.template.plc.edit.slaveStationNo'
                })}
                name='slaveStationNo'
              >
                <Input />
              </Form.Item>
            )}
          </Col>
        </Row>

        {type != 'local' && (
          <Collapse
            destroyInactivePanel={false}
            defaultActiveKey={['app.template.plc.panel.1']}
          >
            <Panel
              forceRender={true}
              key='app.template.plc.panel.1'
              header={formatMessage({
                id: 'app.template.plc.edit.workModel'
              })}
            >
              {type == 'serial' && (
                <TemplatePlcSerial
                  portNoItems={props.formData?.portNoItems || portNoItems}
                />
              )}
              {type == 'ethernet' && (
                <TemplatePlcEthernet deviceMode={deviceMode} />
              )}
            </Panel>

            {(type == 'serial' || (type == 'ethernet' && deviceMode == 0)) && (
              <Panel
                forceRender={true}
                key='app.template.plc.panel.2'
                header={formatMessage({
                  id: 'app.template.plc.edit.advance'
                })}
              >
                <TemplatePlcAdvanced />
              </Panel>
            )}
          </Collapse>
        )}

        {disabled && (
          <Row gutter={24}>
            <Col span={24}>
              <span style={{ color: 'red' }}>
                {formatMessage({
                  id: 'app.template.plc.edit.references',
                  parameters: { refs: getRefTypeDesc(props.references!) }
                })}
              </span>
              <a
                onClick={() => {
                  setReferenceVisible(true)
                }}
              >
                {formatMessage({ id: 'app.template.reference.view' })}
              </a>
            </Col>
          </Row>
        )}
      </Form>
      <TemplateReference
        data={props.references}
        visible={referenceVisible}
        onVisibleChange={setReferenceVisible}
        onRefRelease={props.onRefRelease}
      />
    </div>
  )
}
